import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { DownloadCloud, Loader2, CheckCircle2, Trash2, MapPin, Building2, CalendarClock, RefreshCw, Languages } from "lucide-react";
import { AdminHeader } from "../../components/AdminHeader";
import { adminApi, clearToken, getToken } from "../../lib/adminApi";

export default function AdminImport() {
  const nav = useNavigate();
  const [staged, setStaged] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [status, setStatus] = useState({ running: false });
  const [publishing, setPublishing] = useState(false);
  const [trans, setTrans] = useState({ running: false, pending: 0 });
  const poll = useRef(null);
  const transPoll = useRef(null);

  const loadStaged = useCallback(async () => {
    try {
      const { data } = await adminApi.importStaged();
      setStaged(data);
    } catch (err) {
      if (err.response?.status === 401) { clearToken(); nav("/admin"); }
      else toast.error("Kon staged projecten niet laden");
    } finally { setLoading(false); }
  }, [nav]);

  const checkStatus = useCallback(async () => {
    try {
      const { data } = await adminApi.importStatus();
      setStatus(data);
      if (!data.running) {
        if (poll.current) { clearInterval(poll.current); poll.current = null; }
        await loadStaged();
      }
    } catch { /* ignore */ }
  }, [loadStaged]);

  useEffect(() => {
    if (!getToken()) { nav("/admin"); return; }
    loadStaged();
    checkStatus();
    checkTrans();
    return () => { if (poll.current) clearInterval(poll.current); if (transPoll.current) clearInterval(transPoll.current); };
  }, [loadStaged, checkStatus, nav]);

  const checkTrans = useCallback(async () => {
    try {
      const { data } = await adminApi.translateStatus();
      setTrans(data);
      if (!data.running && transPoll.current) { clearInterval(transPoll.current); transPoll.current = null; }
    } catch { /* ignore */ }
  }, []);

  const startTranslate = async () => {
    try {
      await adminApi.translateProjects();
      toast.success("Projecten worden naar het Nederlands vertaald…");
      setTrans((s) => ({ ...s, running: true }));
      if (transPoll.current) clearInterval(transPoll.current);
      transPoll.current = setInterval(checkTrans, 3000);
    } catch (err) {
      if (err.response?.status === 401) { clearToken(); nav("/admin"); }
      else toast.error("Vertalen mislukt");
    }
  };

  const startScan = async () => {
    try {
      await adminApi.importScan(80);
      toast.success("Bezig met ophalen van de nieuwste projecten…");
      setStatus((s) => ({ ...s, running: true, done: 0, total: 80 }));
      if (poll.current) clearInterval(poll.current);
      poll.current = setInterval(checkStatus, 2000);
    } catch (err) {
      if (err.response?.status === 401) { clearToken(); nav("/admin"); }
      else toast.error("Ophalen mislukt");
    }
  };

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const allSelected = staged.length > 0 && staged.every((p) => selected[p.id]);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(staged.map((p) => [p.id, true])));
  };
  const selectedIds = staged.filter((p) => selected[p.id]).map((p) => p.id);

  const publish = async () => {
    if (selectedIds.length === 0) { toast.error("Selecteer minstens één project"); return; }
    setPublishing(true);
    try {
      const { data } = await adminApi.importPublish(selectedIds);
      toast.success(`${data.published} project(en) gepubliceerd op de site`);
      setSelected({});
      await loadStaged();
    } catch (err) {
      if (err.response?.status === 401) { clearToken(); nav("/admin"); }
      else toast.error("Publiceren mislukt");
    } finally { setPublishing(false); }
  };

  const dismiss = async (id) => {
    try { await adminApi.importDismiss(id); setStaged((s) => s.filter((p) => p.id !== id)); }
    catch { toast.error("Verwijderen mislukt"); }
  };
  const clearAll = async () => {
    if (!window.confirm("Alle opgehaalde concepten verwijderen?")) return;
    try { await adminApi.importDismiss("all"); setStaged([]); setSelected({}); }
    catch { toast.error("Legen mislukt"); }
  };

  const progress = status.total ? Math.round((status.done / status.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-cream">
      <Toaster position="top-right" richColors />
      <AdminHeader />
      <main className="mx-auto max-w-6xl px-5 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-semibold text-navy">Projecten importeren</h1>
            <p className="mt-1 text-sm text-navy/60 max-w-xl">
              Haal de nieuwste off-plan projecten op, bekijk ze als concept en kies zelf welke je online zet.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={startTranslate} disabled={trans.running} data-testid="translate-btn"
              className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-4 py-2.5 text-sm font-medium text-navy/70 transition hover:bg-navy/5 disabled:opacity-60">
              {trans.running ? <><Loader2 size={15} className="animate-spin" /> Vertalen…</> : <><Languages size={15} /> Vertaal naar NL{trans.pending ? ` (${trans.pending})` : ""}</>}
            </button>
            {staged.length > 0 && (
              <button onClick={clearAll} data-testid="import-clear-all"
                className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-4 py-2.5 text-sm font-medium text-navy/70 transition hover:bg-navy/5">
                <Trash2 size={15} /> Concepten legen
              </button>
            )}
            <button onClick={startScan} disabled={status.running} data-testid="import-scan-btn"
              className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-navy/90 disabled:opacity-60">
              {status.running ? <><Loader2 size={16} className="animate-spin" /> Bezig…</> : <><RefreshCw size={16} /> Nieuwste projecten ophalen</>}
            </button>
          </div>
        </div>

        {status.running && (
          <div className="mt-6 rounded-2xl bg-white p-5 shadow-soft" data-testid="import-progress">
            <div className="flex items-center justify-between text-sm text-navy/70">
              <span>Projecten ophalen en verwerken…</span>
              <span>{status.done || 0} / {status.total || 80}</span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-navy/10">
              <div className="h-full rounded-full bg-gold transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {staged.length > 0 && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white px-5 py-3 shadow-soft">
            <label className="inline-flex items-center gap-2 text-sm font-medium text-navy" data-testid="import-select-all">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-gold" />
              Alles selecteren ({selectedIds.length}/{staged.length})
            </label>
            <button onClick={publish} disabled={publishing || selectedIds.length === 0} data-testid="import-publish-btn"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-gold/90 disabled:opacity-50">
              {publishing ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              Geselecteerde publiceren
            </button>
          </div>
        )}

        {loading ? (
          <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-navy/40" size={30} /></div>
        ) : staged.length === 0 && !status.running ? (
          <div className="mt-16 flex flex-col items-center text-center" data-testid="import-empty">
            <DownloadCloud size={40} className="text-navy/25" />
            <p className="mt-3 text-navy/60">Nog geen opgehaalde projecten. Klik op <b>Nieuwste projecten ophalen</b> om te starten.</p>
          </div>
        ) : (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="import-grid">
            {staged.map((p) => (
              <div key={p.id} data-testid={`import-card-${p.id}`}
                className={`group overflow-hidden rounded-2xl bg-white shadow-soft transition ${selected[p.id] ? "ring-2 ring-gold" : ""}`}>
                <div className="relative h-44 w-full overflow-hidden bg-navy/5">
                  {p.image ? (
                    <img src={p.image} alt={p.name} className="h-full w-full object-cover" loading="lazy"
                      onError={(e) => { e.target.style.display = "none"; }} />
                  ) : <div className="flex h-full items-center justify-center text-navy/30"><Building2 size={30} /></div>}
                  <label className="absolute left-3 top-3 flex h-7 w-7 cursor-pointer items-center justify-center rounded-md bg-white/90 shadow">
                    <input type="checkbox" checked={!!selected[p.id]} onChange={() => toggle(p.id)}
                      data-testid={`import-check-${p.id}`} className="h-4 w-4 accent-gold" />
                  </label>
                  <span className="absolute right-3 top-3 rounded-full bg-navy/85 px-2.5 py-1 text-[11px] font-semibold text-white">{p.tag}</span>
                </div>
                <div className="p-4">
                  <h3 className="font-display text-lg font-semibold text-navy leading-tight">{p.name}</h3>
                  {p.developer && <p className="mt-0.5 text-xs text-navy/50">door {p.developer}</p>}
                  <div className="mt-2 space-y-1 text-sm text-navy/70">
                    {p.location && <p className="flex items-center gap-1.5"><MapPin size={13} className="text-gold" /> {p.location}</p>}
                    {p.bedrooms && <p className="flex items-center gap-1.5"><Building2 size={13} className="text-gold" /> {p.bedrooms} slaapkamers</p>}
                    {p.handover && <p className="flex items-center gap-1.5"><CalendarClock size={13} className="text-gold" /> Oplevering {p.handover}</p>}
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="font-semibold text-gold">{p.price || "Prijs op aanvraag"}</span>
                    <button onClick={() => dismiss(p.id)} data-testid={`import-dismiss-${p.id}`}
                      className="text-navy/30 transition hover:text-red-500"><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
